/* eslint-disable max-lines */
import {observer} from 'mobx-react-lite'
import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {HTMLUtil, logger, UIUtil} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  noBoardContainerId,
  Subtask,
  Tag,
  TaskColor,
  TimetrackerValue
} from 'yougile-model'
import {UiSlot} from '@api/components'
import {Items} from '@api/model/items'
import {ContextTaskProvider} from '@api/shared/context/context-task'
import {UiLocationProvider} from '@api/shared/context/ui-location'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {DragData} from '@common/drag-data'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {useBoardStore} from '@components/board/board-store'
import {useColumnStore} from '@components/columns/column-store'
import {ErrorBoundary} from '@components/common/error-boundary/error-boundary'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {DealSubtitle} from '@components/deal-subtitle/deal-subtitle'
import {useDnDStore} from '@components/draggable'
import {IdTasksTaskCardSticker} from '@components/id-tasks/id-tasks-task-card-sticker'
import {MessageBox} from '@components/message-box'
import {MoveToBody} from '@components/move-to-body'
import {multiActionsWithTasksStore as multiStore} from '@components/multi-actions-with-tasks/multi-actions-with-tasks-store'
import {useTaskFromHubWithEffect} from '@components/my-tasks/task-item'
import {ShareTaskStats} from '@components/sharing/share-task-stats/share-task-stats'
import {TaskStickers} from '@components/sticker/task-stickers'
import {ColumnParentTasks} from '@components/subtasks-old/parent-tasks'
import {TaskSubtasks} from '@components/subtasks-old/subtasks'
import {DoneButton} from '@components/task-card/done-button'
import {TaskImage} from '@components/task-image'
import {MoveTemplatesMenu} from '@components/task-menu/move-templates-menu'
import {
  dealMenuVisibleItems,
  TaskMenuNew,
  TaskMenuVisibleItems
} from '@components/task-menu/task-menu-new'
import {TaskTimeline} from '@components/task-timeline/task-timeline'
import {UiElementFor} from '@components/ui-element-for'
import {updateRef} from '@containers/element-utils'
import {
  noPropagate,
  useBoolean,
  useForceUpdate,
  useLoadedLazyTaskEffect,
  useMemoOne,
  useSyncEvent
} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {QueuedRender} from '@containers/queued-render'
import {ChatUtils} from '@extensions/chat-utils'
import {Column} from '@extensions/column'
import {Feedback} from '@extensions/feedback'
import {globals} from '@extensions/globals-base'
import {isMirroredHub} from '@extensions/mirrored-tasks/is-mirrored-hub'
import {canShowTask} from '@extensions/mirrored-tasks/permissions'
import {TaskCardMirrorIcon} from '@extensions/mirrored-tasks/task-card-mirror-icon'
import {PageService} from '@extensions/page-service'
import {Task} from '@extensions/task'
import {taskPhantomObserver} from '@extensions/task-phantom-observer'
import {crmStore} from '../crm/crm-store'
import {DealActivityCard} from './deal-activity-card'
import {Locations} from './locations'
import {NoBoard} from './no-board'
import {ShowParentTasks} from './show-parent-tasks'
import {
  TaskCardContext,
  TaskCardStore,
  useTaskCardStore
} from './task-card-store'
import {TaskChecklists} from './task-checklists'
import {taskConnectionStore} from './task-connection-store'
import {TaskTitle} from './task-title'

export interface TaskCardProps {
  task: Hub
  className?: string
  style?: React.CSSProperties
  onClick?: (e: React.MouseEvent) => void
  onMouseDown?: (e: React.MouseEvent) => void
  remove?: () => void
  onAfterTitleUpdate?: (title: string) => void
  withLocationTooltip?: boolean
  asSubtask?: boolean
  noShadow?: boolean
  setLocation?: (locationId: DataObjectId) => void
  onBeforeOpenChat?: () => void
  containerId?: DataObjectId
  noCheckLists?: boolean
  noDoneButton?: boolean
  noSubtasks?: boolean
  taskMenuVisibleItems?: TaskMenuVisibleItems[]
  noMovementTemplates?: boolean
  movementAnchorIcon?: boolean
  withBorder?: boolean
  isParentTaskTemplate?: boolean
  boardId?: DataObjectId
  placeholder?: Subtask | undefined
  parentTaskId?: DataObjectId
  noTaskColor?: boolean
}

// todo: should use a `useStore` hook here but this requires wrapping the
//  whole application w/ that context (logged-in component) b/c tasks are
//  used both on board and in right panel; using store as a global var w/o
//  wrapping component tree in that store's context may result in loss of
//  some store updates
const multiState = multiStore.state

const TaskCardBase = observer(
  React.forwardRef(
    // eslint-disable-next-line max-lines-per-function
    (
      {task, ...props}: TaskCardProps,
      forwardedRef: ForwardedRef<HTMLElement>
    ) => {
      const {
        movementAnchorIcon = true,
        isParentTaskTemplate,
        placeholder
      } = props
      const [state, store] = useTaskCardStore()
      const [, columnStore] = useColumnStore()
      const [boardState, boardStore] = useBoardStore()
      const isInBoard = !!boardState

      const forceUpdate = useForceUpdate()
      const [selectedInMulti, setSelectedInMulti] = useState(
        multiState.visible && multiState.tasksIds.has(task.id)
      )
      const taskContainer = useRef<HTMLDivElement>()
      useLoadedLazyTaskEffect(task, 'task-card')

      useSyncEvent(
        'current',
        {
          filter: [task?.id, props.parentTaskId] as DataObjectId[],
          debounceTime: 100
        },
        () => store.syncOnUpdate(props.boardId),
        [task, props.boardId, props.parentTaskId]
      )

      useSyncEvent(
        'current',
        {
          filter: [state.boardId, props.boardId] as DataObjectId[],
          debounceTime: 100
        },
        () => {
          store.syncOnBoardUpdate(props.boardId)
          forceUpdate()
          store.syncOnBoardSettings(props.boardId)
        },
        [state.boardId, props.boardId]
      )

      useLayoutEffect(() => {
        store.syncOnBoardSettings(props.boardId)
      }, [state.boardId, props.boardId])

      useLayoutEffect(() => {
        setSelectedInMulti(
          multiState.visible && multiState.tasksIds.has(task.id)
        )
      }, [multiState.tasksIds.size])

      const box = Sync.getCurrentSandbox()
      const taskItem = useTaskFromHubWithEffect(box, task)

      const taskConnectionState = taskConnectionStore.state

      const taskToConnect = taskConnectionState.taskToConnect
      const isTaskToConnect = !!taskConnectionState.taskToConnect
      const isCurrentTaskToConnect = taskToConnect?.id === task.id

      const taskInColumn = !!props.containerId
      const selected = !selectedInMulti && state.rightPanelOpen

      const [edit, setEdit] = useState(false)

      // Subtasks have their own state but also must be controlled from outside b/c of the
      // "add subtask" menu item; there's also an optimization to not create a subtask store
      // when they are not rendered, that's why a combination of external&internal state is used
      const toggleSubtasksRef = useRef<() => void>()
      const subtasksInitialOpen = useBoolean(false)
      const addSubtask = () => {
        toggleSubtasksRef.current?.()
        subtasksInitialOpen.turnOn()
      }

      const startRename = () => {
        setEdit(true)
      }

      const dragStart = (e: React.DragEvent) => {
        if (props.asSubtask && multiState.visible) {
          noPropagate(e)
          MessageBox.warn(Lang.msg.multiActionsWithTasks.noDragTask)
          return
        }
      }

      const onClick = (e: React.MouseEvent) => {
        e.stopPropagation()

        if (props.isParentTaskTemplate) {
          return
        }

        if (props.asSubtask && !canShowTask(task)) {
          MessageBox.warn(Lang.msg.subtask.noPermissionForTask)
          return
        }

        if (taskToConnect && taskToConnect.id !== task.id) {
          if (Hub.isConnected(taskToConnect, task)) {
            MessageBox.warn(Lang.msg.alreadyConnected)
          } else {
            Hub.connect(taskToConnect, task)
          }

          taskConnectionStore.clear()

          globals.taskToConnect = undefined
          globals.taskToConnectType = undefined
          globals.taskToConnectUpdated?.()
          return
        }

        if ((e.metaKey || e.ctrlKey) && !multiState.visible) {
          multiStore.visibleOn()
          void Tracking.send(
            TrackingEvent.multiActionsWithTasksSelectWithHotKey
          )
        }

        if (multiState.visible) {
          multiStore.setTasksId(task.id)
          return
        }

        if (UIUtil.isConsumed(e) || !Items.click(Items.fromHub(task))) {
          return
        }

        props.onBeforeOpenChat?.()
        void Tracking.send(TrackingEvent.taskOpenTaskRightPanelViaBoard)
        ChatUtils.toggleChat(task)

        props.onClick?.(e)
      }

      const setLocation = (id: DataObjectId) => {
        const hub = box.getHub(id)
        hub &&
          Column.taskDropHandler(0, task, hub, {
            ignoreBoardFilters: true,
            type: 'menu'
          })
      }

      const taskRef = useRef<HTMLElement>(null)
      const {
        placeholder: subtaskPlaceholder,
        dropBefore,
        setShowBottomDropzone,
        setShowTopDropzone
      } = useSubtaskPlaceholder(taskRef)

      const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        const type = DragData.get()?.type
        const types = ['user', 'sticker', 'task', 'checklist']

        if (
          !type ||
          !types.includes(type) ||
          // A subtask can be dragged over a task located in either the basic or a mirror column
          (type === 'task' && !taskInColumn)
        ) {
          return
        }

        event.preventDefault()
        event.stopPropagation()
        DragData.setEffect(event, 'copy')

        if (type === 'task') {
          const rect = event.currentTarget.getBoundingClientRect()
          const center = rect.top + rect.height / 2
          const onTop = center > event.clientY
          setShowTopDropzone(onTop)
          setShowBottomDropzone(!onTop)
        }
      }

      const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        setShowTopDropzone(false)
        setShowBottomDropzone(false)
        const type = DragData.get()?.type
        if (type === 'user' || type === 'sticker' || type === 'checklist') {
          event.preventDefault()
          DragData.setEffect(event, 'none')
        }
      }

      const hideShowGroupClassName = () => {
        // to avoid "blinking" -> block all elements which appear on hover for some ms
        if (taskContainer.current) {
          taskContainer.current.classList.remove('group')
        }
        setTimeout(() => {
          if (taskContainer.current) {
            taskContainer.current.classList.add('group')
          }
        }, 100)
      }

      const onDrop = () => {
        hideShowGroupClassName()

        Task.onDrop({
          taskId: task.id,
          before: dropBefore,
          containerId: props.containerId,
          taskDropAllowed: taskInColumn,
          type: 'drag'
        })

        setShowTopDropzone(false)
        setShowBottomDropzone(false)
        DragData.setCurrent({type: 'empty'})
        // column store may not be available when a task is rendered outside the Column component
        columnStore?.syncOnUpdate()
      }

      const [showParentTasks, setShowParentTasks] = useState(false)

      const draggable = props.asSubtask && multiState.visible

      const feedbackUserData = Feedback.getFeedbackUserData(task)

      const onCloseRequiredTimeTrackingSticker = (sticker: Tag) => {
        const currentTask = Task.getById(task.id)
        if (currentTask) {
          const stickerValue = currentTask.stickers[sticker.id] as
            | TimetrackerValue
            | undefined
          boardStore.removeStickerFromRequireTimeTracking(task, stickerValue)
        }
      }

      const isDeal = crmStore.isDeal(task)

      const noCheckLists = props.noCheckLists || isDeal
      const noSubtasks = props.noSubtasks || isDeal
      const noMovementTemplates = props.noMovementTemplates || isDeal
      const noDoneButton = props.noDoneButton || isDeal

      const hasSubtasksMarginLeft =
        !noSubtasks && !noCheckLists && !noDoneButton

      let taskMenuVisibleItems: TaskMenuVisibleItems[] =
        props.taskMenuVisibleItems

      if (isDeal) {
        taskMenuVisibleItems = dealMenuVisibleItems
      }

      return (
        <UiLocationProvider location="task-card">
          <ContextTaskProvider taskId={task.id}>
            <ErrorBoundary
              silent
              onError={e => logger.error('task-card error ' + e.message)}
            >
              <Tooltip
                disabled={!props.withLocationTooltip}
                placement="left"
                content={
                  isParentTaskTemplate ? undefined : (
                    <Locations {...props} task={task} />
                  )
                }
              >
                <TwTaskCardContainer
                  data-test="board-task-card"
                  ref={el => {
                    updateRef(el, taskRef)
                    updateRef(el, forwardedRef)
                    store.taskContainerRefCallback(el)
                  }}
                  data-task-id={task.id}
                  $taskToConnect={isTaskToConnect && !isCurrentTaskToConnect}
                  $selected={selected}
                  $new={state.isNew}
                  $withBorder={props.withBorder}
                  $selectedMultipleTaskId={selectedInMulti}
                  className={cn(
                    props.className,
                    task.isLazy && 'pointer-events-none'
                  )}
                  style={props.style}
                  onDrop={onDrop}
                  onClick={onClick}
                  onMouseDown={e => {
                    draggable && e.stopPropagation()
                    props.onMouseDown?.(e)
                  }}
                  draggable={draggable}
                  onDragStart={dragStart}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                >
                  {selected && <TwSelected />}
                  <TwUiElementFor objId={task.id}>
                    <TwTaskCardContainerInternal
                      $color={isInBoard ? task.data.color : undefined}
                    >
                      <TaskImage
                        hub={task}
                        isAutoTaskCover={columnStore?.isAutoTaskCover}
                      />
                      <TwTaskContainer
                        ref={taskContainer}
                        data-test="TwTaskContainer"
                      >
                        <TwTask data-test="TwTaskContainer">
                          {!noDoneButton && (
                            <DoneButton
                              hub={task}
                              fromWhere="board"
                              isParentTaskTemplate={isParentTaskTemplate}
                              placeholder={placeholder}
                              isTaskColored={Task.isTaskColored(task)}
                            />
                          )}
                          <TaskTitle
                            data-test="TaskTitle"
                            canRename={state.canRename}
                            updateTitle={title => {
                              title = HTMLUtil.trimLineBreaks(title)
                              setEdit(false)
                              if (
                                task &&
                                Items.rename(Items.fromHub(task), title)
                              ) {
                                box.editTitle(task, title, () => {
                                  taskPhantomObserver.update()
                                })
                                props.onAfterTitleUpdate?.(title)
                              }
                            }}
                            task={task}
                            placeholder={placeholder}
                            edit={edit}
                            setEdit={setEdit}
                            completed={state.isCompleted}
                            isTaskColored={
                              !props.noTaskColor && Task.isTaskColored(task)
                            }
                          />
                          {taskItem && (
                            <>
                              {!noMovementTemplates &&
                                movementAnchorIcon &&
                                !isMirroredHub(task) && (
                                  <MoveTemplatesMenu
                                    taskId={taskItem.id}
                                    completed={state.isCompleted}
                                    isTaskColored={Task.isTaskColored(task)}
                                  />
                                )}

                              <TaskMenuNew
                                task={taskItem}
                                rename={startRename}
                                addSubtask={() =>
                                  PermissionsChecker.checkEditSubtasks(task) &&
                                  addSubtask()
                                }
                                removeFromSubtasks={props.remove}
                                visibleItems={taskMenuVisibleItems}
                                duplicate={taskInColumn}
                                fromWhere="board"
                                removeFromBoard={
                                  taskInColumn && state.isSubtask
                                }
                                className="-m-4"
                                completed={state.isCompleted}
                                isParentTaskTemplate={isParentTaskTemplate}
                                asSubtask={props.asSubtask}
                              />
                            </>
                          )}
                        </TwTask>

                        {isDeal && (
                          <DealSubtitle deal={task} className="mt-4 ml-4" />
                        )}

                        <div
                          className={cn(
                            'flex relative mt-8 mr-[40px] flex-wrap min-h-24',
                            hasSubtasksMarginLeft ? 'ml-20' : '',
                            !Task.isTaskColored(task) &&
                              state.isCompleted &&
                              'opacity-60 group-hover:opacity-100'
                          )}
                        >
                          {!props.asSubtask &&
                            task.location === noBoardContainerId && (
                              <NoBoard
                                setLocation={setLocation}
                                isSubtask={state.isSubtask}
                              />
                            )}

                          {!noSubtasks &&
                            !props.asSubtask &&
                            (state.hasSubtasks || state.isSubtask ? (
                              <ShowParentTasks
                                task={task}
                                className="absolute -left-20 top-4"
                                opened={showParentTasks}
                                completed={state.isCompleted}
                                isTaskColored={Task.isTaskColored(task)}
                                onToggle={() =>
                                  setShowParentTasks(state => !state)
                                }
                              />
                            ) : (
                              <TaskCardMirrorIcon
                                className="absolute -left-20 top-4"
                                task={task}
                                isCompleted={state.isCompleted}
                                isTaskColored={Task.isTaskColored(task)}
                                onToggle={() =>
                                  setShowParentTasks(state => !state)
                                }
                              />
                            ))}

                          {!!feedbackUserData?.id && (
                            <Tooltip content="Email" placement="bottom">
                              <div
                                className="sticker-item pl-6 mt-2 mr-6"
                                onMouseDown={noPropagate}
                                onClick={noPropagate}
                              >
                                <div className="sticker-item-text">
                                  {feedbackUserData.email || '???'}
                                </div>
                              </div>
                            </Tooltip>
                          )}

                          <QueuedRender
                            id={columnStore?.column?.location}
                            queue={boardState?.tasksRenderQueue}
                            placeholderClassName="h-24"
                          >
                            <TaskStickers
                              task={store.currentTask}
                              requireTimeTrackingBeforeClose={
                                store.currentTask &&
                                boardState?.requireTimeTrackingBeforeClose.get(
                                  store.currentTask.id
                                )
                              }
                              onCloseRequiredTimeTrackingSticker={
                                onCloseRequiredTimeTrackingSticker
                              }
                              columnId={columnStore?.column?.id}
                              pinnedTags={store.pinnedStickers}
                              isParentTaskTemplate={isParentTaskTemplate}
                              asSubtask={props.asSubtask}
                              firstChildren={
                                state.showIdOnTaskCard ? (
                                  <IdTasksTaskCardSticker
                                    task={task}
                                    isCompleted={state.isCompleted}
                                    isTaskColored={Task.isTaskColored(task)}
                                  />
                                ) : undefined
                              }
                              isCompleted={state.isCompleted}
                              isInBoard={isInBoard}
                              taskInColumn={taskInColumn}
                            >
                              {taskInColumn && state.showMessageStats && (
                                <div
                                  onMouseDown={noPropagate}
                                  onClick={noPropagate}
                                >
                                  <ShareTaskStats
                                    task={task}
                                    showZeroStats={state.showZeroMessagesStats}
                                  />
                                </div>
                              )}
                              {taskInColumn && state.showTimeTaskInCol && (
                                <div
                                  onMouseDown={noPropagate}
                                  onClick={noPropagate}
                                >
                                  <TaskTimeline task={task} />
                                </div>
                              )}
                            </TaskStickers>
                          </QueuedRender>
                        </div>
                        {!noCheckLists && !state.hasSubtasks && (
                          <TaskChecklists
                            className={cn(
                              Task.isTaskColored(task) && state.isCompleted
                                ? 'opacity-[.38]'
                                : 'opacity-[.64]',
                              !Task.isTaskColored(task) &&
                                state.isCompleted &&
                                'opacity-60 group-hover:opacity-100',
                              'mt-8'
                            )}
                            titleClassName={cn(
                              Task.isTaskColored(task) && '!text-icon-primary'
                            )}
                            isTaskCompleted={state.isCompleted}
                          />
                        )}
                        {isDeal && !props.asSubtask && (
                          <DealActivityCard deal={task} />
                        )}
                        <UiSlot
                          name="task-card-bottom"
                          data={{
                            taskId: task.id
                          }}
                        />
                      </TwTaskContainer>

                      {showParentTasks && (
                        <ColumnParentTasks
                          onClose={() => setShowParentTasks(false)}
                          task={task}
                          hasSubtask={state.hasSubtasks}
                        />
                      )}

                      <TaskSubtasks
                        task={task}
                        initialOpen={subtasksInitialOpen.value}
                        toggleSubtasksRef={toggleSubtasksRef}
                        onEndAdd={subtasksInitialOpen.turnOff}
                        isParentTaskTemplate={isParentTaskTemplate}
                        boardId={props.boardId}
                      />

                      {taskInColumn && subtaskPlaceholder}
                    </TwTaskCardContainerInternal>
                  </TwUiElementFor>
                </TwTaskCardContainer>
              </Tooltip>
            </ErrorBoundary>
          </ContextTaskProvider>
        </UiLocationProvider>
      )
    }
  )
)

const useSubtaskPlaceholder = (taskRef: React.RefObject<HTMLElement>) => {
  const [showTopDropzone, setShowTopDropzone] = useState(false)
  const [showBottomDropzone, setShowBottomDropzone] = useState(false)
  const [dropzonePosition, setDropzonePosition] = useState({
    left: 0,
    top: 0,
    width: 0
  })
  useEffect(() => {
    if (taskRef.current && (showTopDropzone || showBottomDropzone)) {
      // Need to take into account the displacement of a parent
      const parent = document
        .querySelector('.proj-item')
        ?.getBoundingClientRect()
      const rect = taskRef.current.getBoundingClientRect()
      const left = rect.left - (parent?.left ?? 0)
      const top = rect.top - (parent?.top ?? 0)
      showTopDropzone &&
        setDropzonePosition({left, top: top - 6, width: rect.width})
      showBottomDropzone &&
        setDropzonePosition({
          left,
          top: top + rect.height + 2,
          width: rect.width
        })
    }
  }, [taskRef, showTopDropzone, showBottomDropzone, setDropzonePosition])

  const placeholder = (showTopDropzone || showBottomDropzone) && (
    // display outside the flow to prevent clipping by overflow hidden
    <MoveToBody
      className="bg-action-default h-4 rounded-2"
      top={dropzonePosition.top}
      left={dropzonePosition.left}
      zIndex={10000}
      style={{width: `${dropzonePosition.width}px`}}
      selector=".proj-item"
    />
  )

  return {
    placeholder,
    dropBefore: showTopDropzone,
    setShowTopDropzone,
    setShowBottomDropzone
  }
}

export const TaskCard = observer(
  forwardRef((props: TaskCardProps, ref: ForwardedRef<HTMLElement>) => {
    const taskCardStore = useMemoOne(
      () => new TaskCardStore(props.task, props.isParentTaskTemplate),
      [props.task?.id]
    )

    useEffect(() => {
      return taskCardStore.initialize(props.boardId)
    }, [taskCardStore])

    useSyncEvent(
      'current',
      {
        filter: () => [props.task.id]
      },
      taskCardStore.syncStickers
    )

    useSyncEvent(
      'current',
      {
        debounceTime: 1000,
        filter: () => [PageService.getCurrentBoard()?.id]
      },
      () => {
        taskCardStore.syncStickers()
      }
    )

    useEffect(() => {
      taskCardStore.syncStickers()
    }, [taskPhantomObserver.modified])

    const hidden = useTaskHiddenOnDrag(props.task?.id)
    const className = cn(hidden && 'hidden', props.className)

    return (
      <TaskCardContext.Provider value={taskCardStore}>
        <TaskCardBase {...props} ref={ref} className={className} />
      </TaskCardContext.Provider>
    )
  })
)

const TwTaskCardContainer = tw.div<{
  $taskToConnect?: boolean
  $withBorder?: boolean
  $selectedMultipleTaskId?: boolean
  $new?: boolean
  $selected?: boolean
}>`
  relative
  isolate
  leading-main
  text-main-old
  text-14
  rounded-8
  w-full
  shadow-xs
  hover:shadow-sm
  ${p => {
    const cx: string[] = []
    if (p.$taskToConnect) {
      cx.push('cursor-alias')
    } else {
      cx.push('cursor-pointer')
    }
    let hoverBorder = true
    if (!p.$selected) {
      if (p.$new) {
        cx.push('outline', 'outline-1', 'outline-notification-primary')
      } else if (p.$selectedMultipleTaskId) {
        cx.push('outline', 'outline-2', 'outline-action-default')
        hoverBorder = false
      } else if (p.$withBorder) {
        cx.push('outline', 'outline-1', 'outline-line-base')
      }
    } else {
      if (p.$withBorder) {
        cx.push('outline', 'outline-1', 'outline-line-base')
      } else {
        hoverBorder = false
      }
    }
    if (hoverBorder) {
      cx.push('hover:outline', 'hover:outline-1', 'hover:outline-line-hover')
    }
    return cx.join(' ')
  }}
`

const TwSelected = tw.div`
  absolute
  bg-action-default
  top-0
  bottom-0
  w-12
  -left-4
  -z-10
  rounded-l-12
`

const TwUiElementFor = tw(UiElementFor)``

// Inset shadows are assigned to an internal container to prevent them colliding
// with external task shadow
const TwTaskCardContainerInternal = tw.div<{$color?: TaskColor}>`
  z-10
  rounded-8
  overflow-hidden
  test-data

  ${p => {
    return p.$color ? `bg-${p.$color}` : 'bg-background-card'
  }}
`

const TwTaskContainer = tw.div`
  group
  relative
  p-8
  min-h-64
`

const TwTask = tw.div`
  flex
  gap-4
  align-start
`

const useTaskHiddenOnDrag = (id: DataObjectId): boolean => {
  const [, dndStore] = useDnDStore()

  return useMemo(() => {
    const dndPayload = dndStore.getStarted('task')
    if (!dndStore.state.canBeHiddenOnDrag) {
      return false
    }

    return (
      !!dndPayload && (dndPayload.taskId === id || multiState.tasksIds.has(id))
    )
  }, [id, dndStore.state.dragStarted, dndStore.state.canBeHiddenOnDrag])
}
